import React, { useState, useEffect } from "react";
import {
    Box,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  LocalShipping as TruckIcon,
  Badge as BadgeIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon
} from "@mui/icons-material";
import * as customerServices from "../../services/customer";
import * as salengServices from "../../services/saleng";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import CarSelectDialog from "../../components/CarSelectDialog";
import { formatThaiId } from "../../utils/formatters";;
const { v4: uuidv4 } = require("uuid");

const PASTEL_COLORS = [
  "bg-blue-100 text-blue-600",
  "bg-green-100 text-green-600",
  "bg-purple-100 text-purple-600",
  "bg-orange-100 text-orange-600",
  "bg-pink-100 text-pink-600",
  "bg-indigo-100 text-indigo-600",
  "bg-teal-100 text-teal-600",
  "bg-cyan-100 text-cyan-600",
];

const getRandomColor = (id) => {
    // Simple hash function to get consistent color for same ID
    let hash = 0;
    const str = id || "default";
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % PASTEL_COLORS.length;
    return PASTEL_COLORS[index];
}

function UserList() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState([]); // Logic says this holds object, but initial was []? Correcting to null or {} if single.
  // The original used `[]` for selectedCustomer, but logic treated it as object `filteredData.find(...)`. 
  // Let's inspect `CarSelectDialog` usage: `customerData={selectedCustomer}`.
  
  const [openCarSelectDialog, setOpenCarSelectDialog] = useState(false);
  const navigate = useNavigate();

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerServices.getCustomers('BUYER');
      const data = response.data?.data || [];
      setCustomers(data);
      return data;
    } catch (error) {
      console.error("Error fetching customers:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerPicked = async (data) => {
    const customer = data.customer;
    const car = data.car;
    const driver = data.driver;

    const payload = {
      transactionId: uuidv4(),
      customerId: customer.id,
      carId: car?.id,
      driverId: driver?.id,
      customerName: customer.name,
      status: "waiting",
    };
    const response = await salengServices.createTransaction(payload);
    navigate(
      `/buy/checkout?id=${response.data.data.transactionId}&ticket=${response.data.data.billNumber}&status=waiting`
    );
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const q = searchParams.get("search");
    if (q != null) {
      try {
        setSearch(decodeURIComponent(q));
      } catch {
        setSearch(q);
      }
    }
  }, [searchParams]);

  // helpers
  const includesSafe = (a = "", b = "") =>
    String(a).toLowerCase().includes(String(b).toLowerCase());

  const matchSearch = (customer) => {
    if (!search) return true;

    const keyword = search.toLowerCase();

    const byName = includesSafe(customer.name, keyword);
    const byCarLicense = (customer.cars || []).some((car) =>
      includesSafe(car?.license, keyword)
    );
    const byDriverName = (customer.drivers || []).some((d) => {
      const fullName = [d.name, d.surname].filter(Boolean).join(" ");
      return (
        includesSafe(d.name, keyword) ||
        includesSafe(d.surname, keyword) ||
        includesSafe(fullName, keyword)
      );
    });

    return byName || byCarLicense || byDriverName;
  };

  const filteredData = customers.filter(matchSearch);

  const handleCardClick = async (customerId) => {
    const customer = filteredData.find((item) => item.id === customerId);
    if (!customer) return;

    setSelectedCustomer(customer);
    setOpenCarSelectDialog(true);
  };

  return (
     <Box p={2}>
    <AppBreadcrumbs />
    <div className="h-[calc(100vh-64px)] bg-gray-50 flex flex-col overflow-hidden">

      {/* Fixed Header (Flex Item) */}
      <div className="z-30 bg-white border-b border-gray-200 shadow-sm w-full shrink-0">
         <div className="px-4 py-3 md:px-8 max-w-7xl mx-auto w-full">
            {/* Breadcrumbs - Integrated */}
   

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Left: Title */}
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
                        <GroupsIcon fontSize="medium" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-800 leading-tight">เลือกลูกค้า</h1>
                        <p className="text-xs text-gray-500">เลือกบัญชีเพื่อเริ่มทำรายการรับซื้อ</p>
                    </div>
                </div>

                {/* Right: Search & Actions */}
                <div className="flex items-center gap-3 flex-1 md:flex-none w-full md:w-auto">
                    {/* Search Field */}
                    <div className="relative flex-1 md:w-80 group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#10B981] transition-colors">
                            <SearchIcon />
                        </div>
                        <input
                            type="text"
                            placeholder="ค้นหาชื่อ, ทะเบียนรถ, หรือเบอร์โทร..."
                            className="bg-gray-100 block w-full pl-10 pr-3 py-2.5 rounded-xl border border-transparent text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#10B981] focus:border-transparent sm:text-sm transition-all shadow-inner"
                            value={search}
                            onChange={(e) => setSearch(e.target.value.trimStart())}
                        />
                    </div>
                    
                    {/* Filter Button (Mock) */}
                    <button className="w-11 h-11 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors border border-gray-200">
                        <FilterListIcon />
                    </button>

                    {/* New Customer Button */}
                    <button 
                         onClick={() => navigate(`/customers/create?buy=true`)}
                        className="flex items-center gap-2 bg-gradient-to-br from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white py-2.5 px-5 rounded-xl font-medium shadow-md shadow-green-200 active:scale-95 transition-all whitespace-nowrap"
                    >
                         <AddIcon fontSize="small" />
                         ลูกค้าใหม่
                    </button>
                </div>
            </div>
         </div>
      </div>

      {/* Content Grid (Scrollable) */}
      <div className="flex-1 p-4 md:p-8 w-full overflow-y-auto">
         {loading ? (
             <div className="h-64 flex items-center justify-center">
                 <CircularProgress color="success" />
             </div>
         ) : filteredData.length === 0 ? (
             <div className="text-center py-20">
                 <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
                     <SearchIcon fontSize="large" />
                 </div>
                 <h3 className="text-lg font-medium text-gray-900">ไม่พบลูกค้าที่ค้นหา</h3>
                 <p className="text-gray-500 mt-1">ลองใช้คำค้นหาอื่น หรือสร้างลูกค้าใหม่</p>
             </div>
         ) : (
             <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-6 pb-20">
                 {filteredData.map((item) => {
                     const isSelected = selectedCustomer?.id === item.id;
                     const randomColorClass = getRandomColor(item.id);
                     const initial = item.name ? item.name.charAt(0) : "?";

                     return (
                         <div 
                            key={item.id}
                            onClick={() => handleCardClick(item.id)}
                            className={`
                                group relative bg-white rounded-2xl p-6 cursor-pointer transition-all duration-200 ease-out
                                border flex flex-col h-full
                                ${isSelected 
                                    ? 'border-[#10B981] ring-2 ring-[#10B981] ring-offset-2 shadow-lg z-10' 
                                    : 'border-gray-100 hover:border-[#10B981]/50 hover:shadow-lg hover:-translate-y-1'
                                }
                            `}
                         >
                            {/* Selected Badge */}
                            {isSelected && (
                                <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 bg-[#10B981] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1 z-20">
                                    <CheckCircleIcon sx={{ fontSize: 12 }} />
                                    เลือกอยู่
                                </div>
                            )}

                            {/* Card Header: Avatar & Name */}
                            <div className="flex items-start gap-4 mb-4">
                                <div className={`w-14 h-14 rounded-full shrink-0 flex items-center justify-center text-2xl font-bold ${randomColorClass}`}>
                                    {initial}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-xl font-bold text-gray-800 truncate leading-tight mb-1 group-hover:text-[#10B981] transition-colors">
                                        {item.name}
                                    </h3>
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                                         {item.personType === "normal" ? "บุคคลธรรมดา" : "นิติบุคคล"}
                                    </span>
                                </div>
                            </div>

                            {/* Card Body: Lists */}
                            <div className="flex-1 flex flex-col gap-3">
                                {/* Vehicles */}
                                {item.cars && item.cars.length > 0 && (
                                    <div className="flex flex-col gap-1.5">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">รถที่ลงทะเบียนไว้</p>
                                        {item.cars.slice(0, 3).map(car => (
                                            <div key={car.id} className="flex items-center gap-2 text-sm text-gray-600">
                                                 <TruckIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                                                 <span className="truncate flex-1">
                                                     <span className="font-semibold text-gray-700">{car.license}</span> 
                                                     {car.province && <span className="text-gray-400"> ({car.province})</span>}
                                                     {car.type && <span className="text-gray-400"> - {car.type}</span>}
                                                 </span>
                                            </div>
                                        ))}
                                        {item.cars.length > 3 && (
                                            <span className="text-xs text-gray-400 pl-7">+{item.cars.length - 3} คัน อื่นๆ</span>
                                        )}
                                    </div>
                                )}
                                
                                {(!item.cars || item.cars.length === 0) && (
                                     <div className="flex items-center gap-2 text-sm text-gray-300 italic">
                                         <TruckIcon sx={{ fontSize: 18, opacity: 0.3 }} />
                                         ไม่มีรถลงทะเบียน
                                     </div>
                                )}

                                <div className="h-px bg-gray-50 w-full my-1"></div>

                                {/* Drivers */}
                                {item.drivers && item.drivers.length > 0 ? (
                                    <div className="flex flex-col gap-2 mt-auto">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">คนขับที่ลงทะเบียนไว้</p>
                                        {item.drivers.slice(0, 2).map((d) => (
                                            <div key={d.id} className="flex items-center gap-2 bg-orange-50/50 rounded-lg p-2 border border-orange-100/50">
                                                 <div className="w-6 h-5 rounded bg-orange-100 flex items-center justify-center text-orange-400 shrink-0">
                                                     <BadgeIcon sx={{ fontSize: 14 }} />
                                                 </div>
                                                 <span className="text-sm font-medium text-gray-700 truncate">
                                                     {d.name} {d.surname}
                                                 </span>
                                            </div>
                                        ))}
                                         {item.drivers.length > 2 && (
                                            <span className="text-xs text-gray-400 text-center">+{item.drivers.length - 2} คน อื่นๆ</span>
                                        )}
                                    </div>
                                ) : (
                                    <div className="mt-auto pt-2">
                                        <p className="text-[10px] text-gray-400 text-center bg-gray-50 rounded py-1">ไม่พบคนขับ</p>
                                    </div>
                                )}
                            </div>
                         </div>
                     )
                 })}
             </div>
         )}
      </div>

       {/* Dialog preserved */}
      <CarSelectDialog
        open={openCarSelectDialog}
        onClose={() => setOpenCarSelectDialog(false)}
        customerData={selectedCustomer}
        fetchData={async () => {
          const data = await fetchCustomers();
          const updatedCustomer = data.find(
            (c) => c.id === selectedCustomer.id
          );
          if (updatedCustomer) setSelectedCustomer(updatedCustomer);
        }}
        onSuccess={handleCustomerPicked}
      />
    </div>
    </Box>
  );
}

export default UserList;
